import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import Delete from '@/assets/img/delete-icon.svg?react'
import DotsSvg from '@/assets/img/dots.svg'
import Edit from '@/assets/img/edit-pan.svg?react'
import AddIcon from '@/assets/img/ico-add.svg?react'
import ChatIcon from '@/assets/img/ico-chat.svg?react'
import Button from '@/compoments/common/Button'
import Dropdown from '@/compoments/common/Dropdown'
import Pagination from '@/compoments/common/Pagination'
import { PillStatus } from '@/compoments/common/PillStatus'
import SelectBox from '@/compoments/common/SelectBox'
import UserPart from '@/compoments/common/UserPart'
import UsersGroup from '@/compoments/common/UsersGroup'
import DropdownList from '@/compoments/DropdownList'
import AddTeamModal from '@/compoments/modals/AddTeamModal'
import PageLoader from '@/compoments/PageLoader'
import { getUser } from '@/features/auth/authSlice'
import { useCreateGroupChatMutation } from '@/services/chats/chats'
import {
    useDeleteTeamMutation,
    useGetAllTeamsQuery,
} from '@/services/tenant/teamManagement'
import type { ITeamManagmant } from '@/types/tenant/team.types'
import { optionsTeamStatus } from '@/utils/selectsOptionsConst'

export const AdminTeamManagement = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const queryString = searchParams.toString()
    const { data, isLoading } = useGetAllTeamsQuery(queryString)
    const [deleteTeam] = useDeleteTeamMutation()
    const [editTeamId, setEditTeamId] = useState<number | null>(null)
    const [addTeamModal, setAddTeamModal] = useState<boolean>(false)
    const pageData = data?.data
    const [createGroupChat] = useCreateGroupChatMutation()
    const user = useSelector(getUser)

    const filterHandler = (
        name: string,
        value: string | number | undefined,
    ) => {
        setSearchParams((prev: URLSearchParams) => {
            prev.set('page', '1')
            if (value) {
                prev.set(name, String(value))
            } else {
                prev.delete(name)
            }
            return prev
        })
    }

    const editHandler = (id: number) => {
        setEditTeamId(id)
        setAddTeamModal(true)
    }
    const deleteHandler = async (id: number) => {
        const { toast } = await import('react-toastify')
        try {
            const res = await deleteTeam(id).unwrap()
            if (res?.success) {
                toast.success('Deleted successfully!')
            } else {
                toast.error(res?.message || 'Something went wrong')
            }
        } catch (error) {
            console.error('rejected', error)
        }
    }

    const startChatHandler = async (team: ITeamManagmant) => {
        if (!user?.id) return
        try {
            const res = await createGroupChat({
                name: team.team.name,
                users: [...team.team.members.map(member => member.id), user.id],
            }).unwrap()
            if (res.success) {
                toast.success(res.message || 'Chat created successfully')
            } else {
                toast.error(res.message || 'Something went wrong')
            }
        } catch (error) {
            console.error('rejected', error)
        }
    }

    if (isLoading) return <PageLoader />

    return (
        <main className="content">
            <div className="px-container">
                <div className="table-group">
                    <div className="table-header flex items-center justify-between">
                        <div className="table-tools">
                            <SelectBox
                                options={optionsTeamStatus}
                                placeholder={'Status'}
                                onChange={option =>
                                    filterHandler('status', option?.value)
                                }
                                isClearable={true}
                            />
                        </div>

                        <Button
                            size="sm"
                            variant="primary-gradient"
                            onClick={() => setAddTeamModal(true)}
                        >
                            <AddIcon />
                            <span className="text--xs">Create Team</span>
                        </Button>
                    </div>

                    <div className="table-body table-responsive p-0">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Team</th>
                                    <th>Team Lead</th>
                                    <th style={{ width: '100%' }}>
                                        Team Members
                                    </th>
                                    <th>Status</th>
                                    <th style={{ width: '10px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageData && pageData.length > 0 ? (
                                    pageData.map((team, index) => (
                                        <tr key={index}>
                                            <td>{team.team.name}</td>
                                            <td>
                                                <UserPart
                                                    src={team.team.lead?.photo}
                                                    name={
                                                        team.team.lead?.fullName
                                                    }
                                                    email={
                                                        team.team.lead?.email
                                                    }
                                                />
                                            </td>

                                            <td>
                                                <UsersGroup
                                                    list={team.team.members.map(
                                                        el => ({
                                                            src: el.photo,
                                                        }),
                                                    )}
                                                    usersCount={team.totalUsers}
                                                />
                                            </td>
                                            <td>
                                                <PillStatus
                                                    status={team.team.status}
                                                />
                                            </td>
                                            <td className="text-right sticky-end">
                                                <Dropdown
                                                    closeSelf={true}
                                                    dropdownClasses="flex flex-col"
                                                    button={
                                                        <span className="ico">
                                                            <img
                                                                src={DotsSvg}
                                                                alt=""
                                                            />
                                                        </span>
                                                    }
                                                    menuClassName="dropdown-md"
                                                    menu={
                                                        <DropdownList
                                                            list={[
                                                                {
                                                                    title: 'Edit',
                                                                    icon: (
                                                                        <Edit />
                                                                    ),
                                                                    action: () =>
                                                                        editHandler(
                                                                            team
                                                                                .team
                                                                                .id,
                                                                        ),
                                                                },
                                                                {
                                                                    title: 'Start Team Chat',
                                                                    icon: (
                                                                        <ChatIcon />
                                                                    ),
                                                                    action: async () =>
                                                                        startChatHandler(
                                                                            team,
                                                                        ),
                                                                },
                                                                {
                                                                    title: 'Delete',
                                                                    icon: (
                                                                        <Delete />
                                                                    ),
                                                                    action: async () =>
                                                                        deleteHandler(
                                                                            team
                                                                                .team
                                                                                .id,
                                                                        ),
                                                                },
                                                            ]}
                                                        />
                                                    }
                                                />
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5}>
                                            <h4 className="text-center">
                                                Teams not found
                                            </h4>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <Pagination pagination={data?.pagination} />
            </div>
            {addTeamModal && (
                <AddTeamModal
                    isOpen={addTeamModal}
                    onClose={() => setAddTeamModal(false)}
                    id={editTeamId}
                    setId={setEditTeamId}
                />
            )}
        </main>
    )
}
